// AIドラフト検証のまとめて生成（プロンプトの比較の実行）の API 型（キーはキャメルケース）
import { terminalShortLabel } from "../../terminals"
import { CASE_SET_LABELS } from "../types"
import type { CaseSet, EvaluationRun, GenerationMetadata } from "../types"
import type { Coverage } from "../expectation"

export type BatchStatus = "running" | "completed" | "canceled"

export type BatchTarget = {
  expectedTerminal: string | null
  /** 以前の画面で「検証の進み具合」で絞った実行だけが持つ（今の画面では指定しない） */
  progress: string | null
  /** 要確認の流し直しなら、元のプロンプトの比較の実行 ID */
  sourceBatchId?: number | null
  /**
   * 箱で絞った実行の箱（複数の箱は core,boundary のようにつないだもの）か、判断が分かれるケース以外（except_boundary）。
   * 箱を足す前の実行・すべての種類は null
   */
  caseSet?: string | null
  /** caseSet を箱の並びにしたもの（すべて・判断が分かれるケース以外は null） */
  caseSets?: CaseSet[] | null
  /** お客様の流れを丸ごと流した実行の起点のケース */
  rootCaseId?: number | null
  /** 一覧で選んだケースに絞った実行の目印（同じケースの集まりなら同じ値） */
  selection?: string | null
}

/** 以前の画面で選べた「判断が分かれるケース以外」（境界の箱のケースを除き、種類なしを含む）。過去の実行の表示にだけ使う */
export const EXCEPT_BOUNDARY = "except_boundary"

/** プロンプトの比較で対象に選べる箱。個別はお客様の流れだけで扱うので出さない。既定は定番ケースだけ */
export const REGRESSION_TARGETS: { value: CaseSet; description: string }[] = [
  {
    value: "core",
    description: "プロンプトを変えても結果が変わってほしくない、チャートの各行き先の代表例",
  },
  { value: "boundary", description: "チャートの分かれ目で、どちらに進むか微妙なケース" },
  { value: "real", description: "以前、本番の会話を元に作ったケース" },
]
export const DEFAULT_REGRESSION_TARGETS: CaseSet[] = ["core"]

/** 実行の対象の箱（複数の箱は caseSets、古い実行は caseSet から読む。すべて・判断が分かれるケース以外は null） */
export const targetCaseSets = (target: BatchTarget): string[] | null =>
  target.caseSets ??
  (target.caseSet && target.caseSet !== EXCEPT_BOUNDARY ? target.caseSet.split(",") : null)

/**
 * プロンプトの比較のタブに出す実行：お客様の流れを丸ごと流した実行と、お客様を追うケースを含む実行を除くすべて
 * （個別のケースはお客様の流れのタブだけで扱う）
 */
export const isRegressionRun = (batch: { target: BatchTarget }) =>
  !batch.target.rootCaseId && !(targetCaseSets(batch.target) ?? []).includes("individual")

/** 同じ対象の実行か（箱・期待する対応・要確認の流し直し・お客様の流れ・一覧で選んだケースの集まりがすべて同じ） */
export const sameTarget = (a: BatchTarget, b: BatchTarget) =>
  (a.caseSet ?? null) === (b.caseSet ?? null) &&
  (a.expectedTerminal ?? null) === (b.expectedTerminal ?? null) &&
  (a.sourceBatchId ?? null) === (b.sourceBatchId ?? null) &&
  (a.rootCaseId ?? null) === (b.rootCaseId ?? null) &&
  (a.selection ?? null) === (b.selection ?? null)

export type EvaluationBatch = {
  id: number
  status: BatchStatus
  promptVersionId: number | null
  model: string
  totalCount: number
  startedAt: string
  finishedAt: string | null
  createdBySmootherId: number
  target: BatchTarget
  /** プロンプトの比較の基準にした日時（基準にしていなければ null） */
  baselineAt?: string | null
  /** 「そのまま送れるか」を採点するモデル（判定しない実行は null） */
  gradingModel?: string | null
}

/** 表現の文面ルールのチェックで引っかかった項目。ruleId は本文ルールの ID か required:<id>／forbidden:<id>／max_length */
export type ExpressionIssue = { ruleId: string; severity: "violation" | "caution"; message: string }

/** 表現の文面ルールのチェックの結果。本文のない生成（人手対応・失敗・未完了）は checked が false */
export type ExpressionCheck = {
  checked: boolean
  violationCount: number
  issues: ExpressionIssue[]
}

export type Grade = "send_as_is" | "minor_edit" | "major_edit" | "unsendable"

/** 「そのまま送れるか」の AI による送れるか判定。失敗したときは error だけを持つ */
export type ExpressionGrade = {
  grade?: Grade
  reasons?: string[]
  violatedRules?: string[]
  missingRequired?: string[]
  exemplarMeaningMatch?: "same" | "different" | null
  model?: string
  inputTokens?: number
  outputTokens?: number
  thinkingTokens?: number
  costUsd?: number | null
  gradedAt?: string
  error?: string
}

export const GRADE_LABELS: Record<Grade, string> = {
  send_as_is: "そのまま送れる",
  minor_edit: "軽微な手直し",
  major_edit: "大きな手直し",
  unsendable: "送れない",
}

/** 採点に選べるモデル（既定は設定の gemini-3.8-flash）。モデル ID・提供リージョンは公式モデルカードで確かめたもの */
export const GRADING_MODEL_OPTIONS = ["gemini-3.8-flash", "gemini-3.7-flash"]

/** 本文ルールの一覧（名前・出典） */
export type BodyRule = {
  id: string
  label: string
  source: string
  severity: string
  machine: boolean
}

/**
 * 期待する道筋との比較。AI の道筋（判断記録の steps、無ければ観測の path）のノード ID の並びを、ケースの期待する道筋と
 * 先頭から比べた結果。position は最初にずれた歩（1始まり）、expectedNode・aiNode はその歩のノード（先に終わった側は null）
 */
export type PathComparison = {
  status: "match" | "diverged" | "no_path"
  position: number | null
  expectedNode: string | null
  aiNode: string | null
  expectedNodes: string[]
  aiNodes: string[]
}

export type BatchRun = Pick<
  EvaluationRun,
  "id" | "status" | "body" | "errorMessage" | "durationMs" | "createdAt" | "caseVersion"
> & { generationMetadata: GenerationMetadata | null; expressionGrade?: ExpressionGrade | null }

export type ItemStatus =
  "pending" | "running" | "waiting_retry" | "completed" | "failed" | "canceled"
export type Judgment =
  | "match"
  | "body_match"
  | "mismatch"
  | "no_observation"
  | "failed"
  | "pending"
  | "unset"
export type Verdict = "auto_ok" | "needs_review" | "failed" | "pending" | "unset"
export type Change = "improved" | "worsened" | "strategy_changed" | "same" | "new" | null

export type BatchItem = {
  id: number
  caseId: number
  position: number
  caseTitle: string
  expectedTerminal: string
  status: ItemStatus
  attemptCount: number
  lastError: string | null
  run: BatchRun | null
  observedTerminal: string | null
  judgment: Judgment
  verdict: Verdict
  warnings: { code: string; message: string }[]
  /** ケースに期待する道筋があるときだけの比較結果 */
  path: PathComparison | null
  change: Change
  previousObservedTerminal: string | null
  /** 比べる相手の実行での同じケースの判定（相手に無いケースは null） */
  previousJudgment?: Judgment | null
  /** 期待の形で許した対応（先頭は expectedTerminal） */
  acceptedTerminals?: string[]
  /** 表現の文面ルールのチェック（本文ルール・期待の必須要素・禁止表現・長さ） */
  expression?: ExpressionCheck
  /** 前回の同じケースの表現の違反件数（前回に本文が無ければ null） */
  previousExpressionViolations?: number | null
  coverage?: Coverage | null
  /** ケースの必須要素・禁止表現の名前（ルール ID `required:<id>` → 名前）。ID はケースごとに付ける */
  ruleLabels?: Record<string, string>
}

export type BatchSummary = {
  total: number
  finished: number
  generated: number
  matched: number
  matchRate: number | null
  mismatched: number
  humanReview: number
  failed: number
  canceled: number
  needsReview: number
  autoOk: number
  /** 表現を文面ルールのチェックした件数（本文のある生成）と、そのうち違反のない件数 */
  expressionChecked?: number
  expressionClean?: number
  /** 引っかかった本文ルールごとのケース数（多い順） */
  expressionRuleCounts?: Record<string, number>
  /** 「そのまま送れるか」を採点した件数・判定ごとの件数・費用（米ドル。定価の概算） */
  graded?: number
  gradeCounts?: Partial<Record<Grade, number>>
  gradingCostUsd?: number
}

export type BatchComparison = {
  previousBatchId: number
  previousMatched: number
  previousMatchRate: number | null
  improved: number
  worsened: number
  strategyChanged: number
  previousExpressionClean?: number
  /** 前回より表現の違反が増えたケースの数 */
  expressionWorsened?: number
}

export type BatchDetail = {
  data: EvaluationBatch
  previousBatch: EvaluationBatch | null
  summary: BatchSummary
  comparison: BatchComparison | null
  items: BatchItem[]
  bodyRules?: BodyRule[]
}

/** 採点を待っている生成があるか（判定のモデルがあり、本文のある完了済みの生成に採点結果がまだ無い） */
export const gradingPending = (detail: BatchDetail | undefined) =>
  !!detail?.data.gradingModel &&
  detail.items.some(
    (item) => item.run?.status === "completed" && !!item.run.body && !item.run.expressionGrade
  )

/** 生成を終えてから、採点がこの時間を過ぎても終わらないときは止まったとみなす */
export const GRADING_STALE_MS = 60 * 60 * 1000

/**
 * 採点が止まったとみなす境目の時刻（ミリ秒）。生成を終えた時刻から数え、画面から採点を始めた時刻
 * （gradingStartedAt）がそれより後ならそこから数える。生成を終えていなければ null
 */
export const gradingStaleAt = (
  detail: BatchDetail | undefined,
  gradingStartedAt?: number | null
) => {
  const finishedAt = detail?.data.finishedAt
  if (!finishedAt) return null
  return Math.max(Date.parse(finishedAt), gradingStartedAt ?? 0) + GRADING_STALE_MS
}

/**
 * 採点を待っているのに、生成を終えて（画面から採点を始めていればそこから）GRADING_STALE_MS を過ぎても終わらない（ジョブが止まった等）。
 * 読み直す間隔を空け、画面から採点をもう一度始められるようにする
 */
export const gradingStalled = (
  detail: BatchDetail | undefined,
  now: number = Date.now(),
  gradingStartedAt?: number | null
) => {
  const staleAt = gradingStaleAt(detail, gradingStartedAt)
  return gradingPending(detail) && staleAt != null && now > staleAt
}

/** 表の「表現」列の短い表示（例「違反 2・軽微な手直し」） */
export const expressionLabel = (item: BatchItem) => {
  const parts: string[] = []
  if (item.expression?.checked) {
    if (item.expression.issues.some((issue) => issue.ruleId === "check_failed")) {
      parts.push("チェック失敗")
    } else {
      parts.push(
        item.expression.violationCount ? `違反 ${item.expression.violationCount}` : "違反なし"
      )
    }
  }
  const grade = item.run?.expressionGrade
  if (grade?.grade) parts.push(GRADE_LABELS[grade.grade])
  else if (grade?.error) parts.push("判定失敗")
  return parts.length ? parts.join("・") : "—"
}

/** まとめて生成の指定。プロンプトは保存済みの版か、本文・モデルを直接渡す */
export type StartBatchParams = (
  { promptVersionId: number } | { content: string; model: string }
) & {
  /** 対象の箱（複数選べる。箱の並びはサーバーがそろえる） */
  caseSets?: CaseSet[]
  /** 1つの箱（保存時の自動評価・お客様の流れなど、以前からの指定） */
  caseSet?: CaseSet
  expectedTerminal?: string
  /** この実行で要確認だったケースだけを流し直す */
  sourceBatchId?: number
  /** お客様の流れ（起点のケースと、そこからつながる続きのケース）を丸ごと流す */
  rootCaseId?: number
  /** 一覧で選んだケースだけを流す（しまったケースはサーバーが除く） */
  caseIds?: number[]
  /** 指定すると、始めたあとで「そのまま送れるか」の採点も始める（空文字はサーバの既定モデル）。API には送らない */
  gradingModel?: string
}

/**
 * 結果の絞り込み。プロンプトの比較の画面は 不一致／前回から変化／要確認／一致・警告あり／確認不要／すべて、
 * 1件ずつ試すは 不一致／要確認／すべて を使う
 */
export type ResultFilter =
  "mismatch" | "changed" | "needs_review" | "warning_only" | "auto_ok" | "all"

export const FILTER_LABELS: Record<ResultFilter, string> = {
  mismatch: "不一致",
  changed: "前回から変化",
  needs_review: "要確認",
  warning_only: "一致・警告あり",
  auto_ok: "確認不要",
  all: "すべて",
}

/**
 * プロンプトの比較の結果表の絞り込み。プロンプトを変えたあとは「不一致」と「前回から変化」だけを見れば足りるように、
 * 要確認のうち対応は一致して警告だけのもの（一致・警告あり）を分けて選べるようにする
 */
export const BATCH_FILTERS: ResultFilter[] = [
  "mismatch",
  "changed",
  "needs_review",
  "warning_only",
  "auto_ok",
  "all",
]

/** 要確認に入れる結果。生成失敗も人が対応すべきものなので含める（サマリの要確認件数・要確認の流し直しと同じ） */
export const needsReview = (item: BatchItem) =>
  item.verdict === "needs_review" || item.verdict === "failed"

/** 対応が期待と合った（人の対応推奨の I を含む） */
const matched = (item: BatchItem) => item.judgment === "match" || item.judgment === "body_match"

/** 前回と比べて一致・不一致か、観測した対応が変わった */
export const changedFromPrevious = (item: BatchItem) =>
  item.change === "improved" || item.change === "worsened" || item.change === "strategy_changed"

export const matchesFilter = (item: BatchItem, filter: ResultFilter) => {
  switch (filter) {
    case "needs_review":
      return needsReview(item)
    case "mismatch":
      return item.judgment === "mismatch" || item.judgment === "no_observation"
    case "changed":
      return changedFromPrevious(item)
    case "warning_only":
      return item.verdict === "needs_review" && matched(item)
    case "auto_ok":
      return item.verdict === "auto_ok"
    default:
      return true
  }
}

/**
 * 実行を開いたときの絞り込み。終わった実行は不一致があれば不一致、無ければ要確認、それも無ければすべて。
 * 実行中は届いた順に要確認を見られるよう要確認にする
 */
export const defaultBatchFilter = (items: BatchItem[], running: boolean): ResultFilter => {
  if (running) return "needs_review"
  const order: ResultFilter[] = ["mismatch", "needs_review"]
  return order.find((filter) => items.some((item) => matchesFilter(item, filter))) ?? "all"
}

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  running: "実行中",
  completed: "完了",
  canceled: "中止",
}

export const JUDGMENT_LABELS: Record<Judgment, string> = {
  match: "一致",
  body_match: "本文は一致",
  mismatch: "不一致",
  no_observation: "AIの対応の記録なし",
  failed: "生成失敗",
  pending: "—",
  unset: "—",
}

/** 人手対応（顧客向け本文なし）の観測 */
export const HUMAN_REVIEW = "HUMAN_REVIEW"

/** 対応の突き合わせの結果を一言で：一致／不一致／人手対応／観測なし／生成失敗（未完了は処理の状態） */
export const resultLabel = (item: BatchItem) => {
  if (item.judgment === "pending") return verdictLabel(item)
  if (item.judgment === "match" || item.judgment === "body_match") return "一致"
  if (item.judgment === "mismatch" && item.observedTerminal === HUMAN_REVIEW) return "人手対応"
  return JUDGMENT_LABELS[item.judgment]
}

/** 判定の列。生成が終わっていない項目は処理の状態を出す */
export const verdictLabel = (item: BatchItem) => {
  if (item.verdict === "auto_ok") return "確認不要"
  if (item.verdict === "needs_review") return "要確認"
  if (item.verdict === "failed") return "生成失敗"
  if (item.verdict === "unset") return "—"
  const labels: Record<ItemStatus, string> = {
    pending: "待機中",
    running: "生成中",
    waiting_retry: "再試行待ち",
    completed: "—",
    failed: "生成失敗",
    canceled: "中止",
  }
  return labels[item.status]
}

/** 突き合わせの結果と判定を並べる（例「不一致・要確認」）。同じ言葉になるとき（生成失敗・未完了）は1つだけ */
export const resultSummary = (item: BatchItem) => {
  const result = resultLabel(item)
  const verdict = verdictLabel(item)
  return result === verdict ? result : `${result}・${verdict}`
}

/** 実行の対象の短い表示（例「定番ケース・判断が分かれるケース」「#12 の要確認・すべての種類」「お客様の流れ #5」） */
export const targetLabel = (target: BatchTarget) => {
  const sets =
    target.caseSet === EXCEPT_BOUNDARY
      ? "判断が分かれるケース以外"
      : target.caseSet
        ? (targetCaseSets(target) ?? [])
            .map((value) => CASE_SET_LABELS[value as CaseSet] ?? value)
            .join("・")
        : target.rootCaseId || target.selection
          ? null
          : "すべての種類"
  return [
    ...(target.rootCaseId ? [`お客様の流れ #${target.rootCaseId}`] : []),
    ...(target.selection ? ["選んだケース"] : []),
    ...(target.sourceBatchId ? [`#${target.sourceBatchId} の要確認`] : []),
    ...(sets ? [sets] : []),
    ...(target.expectedTerminal ? [`期待 ${terminalShortLabel(target.expectedTerminal)}`] : []),
    // 以前の画面で進み具合で絞った実行
    ...(target.progress ? ["進み具合で絞り込み"] : []),
  ].join("・")
}

/** 最初にずれた歩の短い表示（例「3歩目 期待 A02／AI R04」）。ずれていなければ null */
export const forkLabel = (path: PathComparison | null) => {
  if (!path) return null
  if (path.status === "no_path") return "AIの道筋の記録なし"
  if (path.status !== "diverged") return null
  return `${path.position}歩目 期待 ${path.expectedNode ?? "なし"}／AI ${path.aiNode ?? "なし"}`
}
